/**
 * Where a visitor came from, remembered for as long as the tab lives.
 *
 * gazar.dev links here from four places and three of them are untagged, so the
 * only tag that reaches this app today is `?src=` on the landing URL. That tag
 * is gone the moment the router replaces the query string, which is long before
 * anyone reaches the signup form, so it is captured on first paint and held.
 *
 * `sessionStorage`, deliberately, not `localStorage`: the question is "what
 * brought this visit", and a source that outlived the tab would keep crediting
 * an old link for every later visit.
 *
 * The alternative was carrying the source to the API and storing it on the
 * account. That needs a change in `apps/backend`, which is shared with two other
 * surfaces, so it is asked for rather than taken. This half needs nobody's
 * permission and answers the same question.
 */
const KEY = "coding.arrival-src";

/** A tag is our own label, so keep it short and boring rather than trusting it. */
const SAFE = /^[a-z0-9_-]{1,40}$/i;

/**
 * Read `?src=` off the current URL and remember it. Call once, before routing.
 * A visit that arrives untagged does not overwrite a tag already held, because
 * the first URL of the visit is the one that explains it.
 */
export function captureArrival(search: string = window.location.search): string | null {
  try {
    const found = new URLSearchParams(search).get("src");
    if (found && SAFE.test(found) && !sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, found);
    }
    return arrivalSource();
  } catch {
    // Private-mode Safari throws on sessionStorage. Losing attribution is not a
    // reason to fail a page load.
    return null;
  }
}

/** The tag this visit arrived with, or `direct` when there was not one. */
export function arrivalSource(): string {
  try {
    return sessionStorage.getItem(KEY) ?? "direct";
  } catch {
    return "direct";
  }
}
